import { Component, Input, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { noop } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  BaseAvatarSize,
  BaseOfferStatus,
  BaseCustomerInquiryOfferInterface,
  CustomerInquiryOfferListItemResponse,
  baseAssignmentsActions,
  BaseCoreState,
  BaseModalService,
  BASE_PERMISSIONS,
  OfferPspBranchShortInfoResponse,
  CustomerInquiryOfferProfileListItemResponse,
  BaseModalHeaderSizes,
  ContractResponse,
  IndustrySurchargeResponse,
  BaseRolesService,
  BaseInquiryService,
  AssignmentCustomerResponse,
  RequirementResponse,
  CustomerInquiryOfferListItemWithOrder,
  BaseInquiryType,
  BaseSigningStatus,
} from 'base';
import { BaseCustomerLocationWorkCouncilSettingsInterface } from '../../../../../shared/_interfaces/base-customer-location-work-council-settings.interface';
import { BaseCustomerOfferActions } from '../../../_enums/base-customer-offer-actions.enum';

@Component({
  selector: 'base-customer-inquiry-unlimited-offer',
  templateUrl: './base-customer-inquiry-unlimited-offer.component.html',
})
export class BaseCustomerInquiryUnlimitedOfferComponent implements OnInit, OnChanges {
  @ViewChild('offerInfoPopup', { static: true }) offerInfoPopup: ElementRef;
  @ViewChild('datePickerModal') datePickerModal: ElementRef;
  @ViewChild('industrySurchargesModal', { static: true }) industrySurchargesModal: ElementRef;
  @ViewChild('requirementsPopup', { static: true }) requirementsPopup: ElementRef;

  @Input() offer: BaseCustomerInquiryOfferInterface | undefined;
  @Input() inquiry: BaseInquiryType;
  @Input() locationWorkCouncilSettings: BaseCustomerLocationWorkCouncilSettingsInterface;
  @Input() assignmentInfo: AssignmentCustomerResponse;
  @Input() validContract: ContractResponse | undefined;

  hasAdminAssignmentsPermission: boolean;
  toOperationTab = false;
  offerAction: BaseCustomerOfferActions;
  offerProfileRequirements: RequirementResponse[] = [];

  avatarSize = BaseAvatarSize;
  offerStatuses = BaseOfferStatus;
  signingStatus = BaseSigningStatus;

  infoModalRef: NgbModalRef;
  datePickerModalRef: NgbModalRef;
  requirementsModalRef: NgbModalRef;
  noteFromProviderModalRef: NgbModalRef;

  isRequirementsFulfilled = false;
  isRequirementsChecked = false;

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private router: Router,
    private rolesService: BaseRolesService,
    public baseInquiryService: BaseInquiryService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.handleRequirements();
  }

  ngOnInit() {
    this.hasAdminAssignmentsPermission = this.rolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_ADMIN_ASSIGNMENTS
    );
  }

  openOfferInfoModal(offer: CustomerInquiryOfferListItemWithOrder, toOperation: boolean = false, event?: Event): void {
    event?.stopPropagation();

    this.toOperationTab = toOperation;

    if (offer.assignment && offer.status === this.offerStatuses.confirmed && this.hasAdminAssignmentsPermission) {
      this.store.dispatch(baseAssignmentsActions.loadAssignment({ payload: { assignment: offer.assignment.id } }));
    }
    this.infoModalRef = this.modalService.open(
      {
        hideHeader: true,
        bodyTemplate: this.offerInfoPopup,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'xl',
      }
    );

    this.infoModalRef.result.then(noop, noop);
  }

  openPositionsDatePickerModal(
    event: Event,
    offer: (CustomerInquiryOfferListItemResponse & { isSelected: boolean; order: number }) | undefined
  ): void {
    event.stopPropagation();

    if (offer?.positions) {
      this.datePickerModalRef = this.modalService.open(
        {
          headText: 'inquiry.offeredDays',
          bodyTemplate: this.datePickerModal,
          okText: 'action.close',
          cancelButtonDisplayed: false,
          cancelButtonOnly: true,
        },
        {
          scrollable: false,
          backdrop: 'static',
          size: 'md',
        }
      );

      this.datePickerModalRef.result.then(noop, noop);
    }
  }

  acceptOrRejectOfferFromInfoModal(event: BaseCustomerOfferActions): void {
    this.offerAction = event;
  }

  openIzsInfoModal(event: Event, pspBranch: OfferPspBranchShortInfoResponse): void {
    event.stopPropagation();

    this.router.navigate(['/service-provider'], {
      queryParams: {
        page: 1,
        search: pspBranch.name,
        pspBranchId: pspBranch.id,
        location: this.inquiry.customerLocation?.id,
      },
    });
  }

  openRequirementsModal(event: Event, profile: CustomerInquiryOfferProfileListItemResponse): void {
    event.stopPropagation();

    this.offerProfileRequirements = this.baseInquiryService.ungroupRequirements(profile.requirements);
    this.requirementsModalRef = this.modalService.open(
      {
        headText: !!this.inquiry.requirements?.length ? 'inquiry.requirementsFor' : 'inquiry.noRequirementsSet',
        headTextTranslateParams: { fullName: `${profile.firstName} ${profile.lastName}` },
        headerSize: BaseModalHeaderSizes.sm,
        bodyTemplate: this.requirementsPopup,
        okText: 'action.close',
        cancelButtonDisplayed: false,
        cancelButtonOnly: true,
      },
      {
        scrollable: false,
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.requirementsModalRef.result.then(noop, noop);
  }

  openIndustrySurchargeModal(event: Event, surcharges?: IndustrySurchargeResponse | null): void {
    event.stopPropagation();

    if (!surcharges) {
      return;
    }

    this.modalService.open(
      {
        headText: 'industrySurcharges.plural',
        bodyTemplate: this.industrySurchargesModal,
        cancelButtonDisplayed: true,
        okButtonDisplayed: false,
      },
      {
        scrollable: false,
        backdrop: 'static',
        size: 'lg',
      }
    );
  }

  openNoteFromProviderModal(
    event: Event,
    offer: (CustomerInquiryOfferListItemResponse & { isSelected: boolean; order: number }) | undefined
  ): void {
    event.stopPropagation();

    if (offer?.note) {
      this.noteFromProviderModalRef = this.modalService.open(
        {
          headText: 'inquiry.noteFromProvider',
          bodyText: offer.note,
          okText: 'action.close',
          cancelButtonDisplayed: false,
          cancelButtonOnly: true,
        },
        {
          scrollable: false,
          backdrop: 'static',
          size: 'lg',
        }
      );

      this.noteFromProviderModalRef.result.then(noop, noop);
    }
  }

  private handleRequirements() {
    if (!this.isRequirementsChecked && !!this.offer && !!this.inquiry) {
      this.isRequirementsFulfilled = this.baseInquiryService.allRequirementsFulfilled(
        this.offer.data.profile.requirements,
        this.inquiry.requirements || []
      );
      this.isRequirementsChecked = true;
    }
  }
}
